import React, { useState, useEffect } from 'react';
import organisationSettingsService, { OrganisationSettings } from '../../services/organisationSettingsService';
import { useTheme } from '../../contexts/ThemeContext';
import { invalidateWhitelabelCache } from '../../hooks/useWhitelabel';
import './WhitelabelSettingsForm.css';

interface WhitelabelSettingsFormProps {
  currentSettings?: OrganisationSettings | null;
  onUpdate?: (settings: OrganisationSettings) => void;
  onError?: (error: string) => void;
}

const WhitelabelSettingsForm: React.FC<WhitelabelSettingsFormProps> = ({
  currentSettings,
  onUpdate,
  onError
}) => {
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Form state
  const [productName, setProductName] = useState('');
  const [primaryLogoFile, setPrimaryLogoFile] = useState<File | null>(null);
  const [primaryLogoPreview, setPrimaryLogoPreview] = useState<string | null>(null);
  const [secondaryLogoFile, setSecondaryLogoFile] = useState<File | null>(null);
  const [secondaryLogoPreview, setSecondaryLogoPreview] = useState<string | null>(null);

  // Load current settings
  useEffect(() => {
    if (currentSettings) {
      setProductName(currentSettings.whitelabelProductName || '');
      
      // Set logo previews from existing URLs
      if (currentSettings.whitelabelPrimaryLogoUrl) {
        const baseUrl = currentSettings.whitelabelPrimaryLogoUrl.startsWith('http')
          ? currentSettings.whitelabelPrimaryLogoUrl
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${currentSettings.whitelabelPrimaryLogoUrl}`;
        setPrimaryLogoPreview(`${baseUrl}?t=${Date.now()}`);
      }
      
      if (currentSettings.whitelabelSecondaryLogoUrl) {
        const baseUrl = currentSettings.whitelabelSecondaryLogoUrl.startsWith('http')
          ? currentSettings.whitelabelSecondaryLogoUrl
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${currentSettings.whitelabelSecondaryLogoUrl}`;
        setSecondaryLogoPreview(`${baseUrl}?t=${Date.now()}`);
      }
    }
  }, [currentSettings]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleLogoSelect = (type: 'primary' | 'secondary', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      showMessage('error', 'Invalid file type. Please select a JPEG, PNG, GIF, WebP, or SVG image.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showMessage('error', 'File is too large. Maximum size is 5MB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'primary') {
        setPrimaryLogoFile(file);
        setPrimaryLogoPreview(e.target?.result as string);
      } else {
        setSecondaryLogoFile(file);
        setSecondaryLogoPreview(e.target?.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async (type: 'primary' | 'secondary') => {
    try {
      setSaving(true);
      await organisationSettingsService.deleteWhitelabelLogo(type);
      
      if (type === 'primary') {
        setPrimaryLogoFile(null);
        setPrimaryLogoPreview(null);
      } else {
        setSecondaryLogoFile(null);
        setSecondaryLogoPreview(null);
      }
      
      // Invalidate cache
      invalidateWhitelabelCache();
      
      showMessage('success', `${type.charAt(0).toUpperCase() + type.slice(1)} logo removed successfully`);
      
      // Refresh settings
      const updatedSettings = await organisationSettingsService.getOrganisationSettings();
      if (onUpdate) onUpdate(updatedSettings);
      
      // Dispatch event for components that need to refresh
      window.dispatchEvent(new CustomEvent('whitelabelUpdated'));
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to remove logo');
      if (onError) onError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProductName = async () => {
    try {
      setSaving(true);
      await organisationSettingsService.updateWhitelabelProductName(productName || null);
      
      // Invalidate cache so other components pick up the new name
      invalidateWhitelabelCache();
      
      showMessage('success', 'Product name updated successfully');
      
      // Refresh settings
      const updatedSettings = await organisationSettingsService.getOrganisationSettings();
      if (onUpdate) onUpdate(updatedSettings);
      
      // Dispatch event for components that need to refresh
      window.dispatchEvent(new CustomEvent('whitelabelUpdated'));
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to update product name');
      if (onError) onError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (type: 'primary' | 'secondary') => {
    const file = type === 'primary' ? primaryLogoFile : secondaryLogoFile;
    if (!file) return;

    try {
      setSaving(true);
      const result = await organisationSettingsService.uploadWhitelabelLogo(type, file);
      
      // Update preview with uploaded URL
      const baseUrl = result.logoUrl.startsWith('http')
        ? result.logoUrl
        : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${result.logoUrl}`;
      
      if (type === 'primary') {
        setPrimaryLogoPreview(`${baseUrl}?t=${Date.now()}`);
        setPrimaryLogoFile(null);
      } else {
        setSecondaryLogoPreview(`${baseUrl}?t=${Date.now()}`);
        setSecondaryLogoFile(null);
      }
      
      // Invalidate cache
      invalidateWhitelabelCache();
      
      showMessage('success', `${type.charAt(0).toUpperCase() + type.slice(1)} logo uploaded successfully`);
      
      // Refresh settings
      const updatedSettings = await organisationSettingsService.getOrganisationSettings();
      if (onUpdate) onUpdate(updatedSettings);
      
      // Dispatch event for components that need to refresh
      window.dispatchEvent(new CustomEvent('whitelabelUpdated'));
    } catch (error: any) {
      showMessage('error', error.message || 'Failed to upload logo');
      if (onError) onError(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="whitelabel-settings-form" data-theme={theme}>
      {message && (
        <div className={`wl-message ${message.type}`}>
          {message.type === 'success' ? '✓' : '✕'} {message.text}
        </div>
      )}

      {/* Product Name Section */}
      <div className="wl-section">
        <div className="wl-section-header">
          <div className="wl-section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div>
            <h4>Product Name</h4>
            <p>Custom name to display instead of "Lumina AI"</p>
          </div>
        </div>
        <div className="wl-input-group">
          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Enter custom product name..."
            maxLength={100}
            disabled={saving}
            className="wl-input"
          />
          <button
            onClick={handleSaveProductName}
            disabled={saving}
            className="wl-btn wl-btn-primary"
          >
            {saving ? 'Saving...' : 'Save Name'}
          </button>
        </div>
        {productName && (
          <div className="wl-preview-text">
            Preview: <strong>{productName}</strong>
          </div>
        )}
      </div>

      {/* Primary Logo Section */}
      <div className="wl-section">
        <div className="wl-section-header">
          <div className="wl-section-icon primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <div>
            <h4>Primary Logo</h4>
            <p>Main logo for headers, login screens, and reports</p>
          </div>
        </div>
        
        <div className="wl-logo-area">
          {primaryLogoPreview ? (
            <div className="wl-logo-preview">
              <img src={primaryLogoPreview} alt="Primary logo preview" />
              <div className="wl-logo-actions">
                <label className="wl-btn wl-btn-secondary">
                  Change
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={(e) => handleLogoSelect('primary', e)}
                    disabled={saving}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  onClick={() => handleRemoveLogo('primary')}
                  disabled={saving}
                  className="wl-btn wl-btn-danger"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="wl-upload-zone">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={(e) => handleLogoSelect('primary', e)}
                disabled={saving}
              />
              <div className="wl-upload-content">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Click to upload primary logo</span>
                <small>JPEG, PNG, GIF, WebP, SVG (max 5MB)</small>
              </div>
            </label>
          )}
          
          {primaryLogoFile && (
            <div className="wl-pending-upload">
              <span>📁 {primaryLogoFile.name}</span>
              <button
                onClick={() => handleUploadLogo('primary')}
                disabled={saving}
                className="wl-btn wl-btn-primary"
              >
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Secondary Logo Section */}
      <div className="wl-section">
        <div className="wl-section-header">
          <div className="wl-section-icon secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <div>
            <h4>Secondary Logo</h4>
            <p>Alternative logo for sidebar, compact views, or different backgrounds</p>
          </div>
        </div>
        
        <div className="wl-logo-area">
          {secondaryLogoPreview ? (
            <div className="wl-logo-preview">
              <img src={secondaryLogoPreview} alt="Secondary logo preview" />
              <div className="wl-logo-actions">
                <label className="wl-btn wl-btn-secondary">
                  Change
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={(e) => handleLogoSelect('secondary', e)}
                    disabled={saving}
                    style={{ display: 'none' }}
                  />
                </label>
                <button
                  onClick={() => handleRemoveLogo('secondary')}
                  disabled={saving}
                  className="wl-btn wl-btn-danger"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="wl-upload-zone">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={(e) => handleLogoSelect('secondary', e)}
                disabled={saving}
              />
              <div className="wl-upload-content">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Click to upload secondary logo</span>
                <small>JPEG, PNG, GIF, WebP, SVG (max 5MB)</small>
              </div>
            </label>
          )}
          
          {secondaryLogoFile && (
            <div className="wl-pending-upload">
              <span>📁 {secondaryLogoFile.name}</span>
              <button
                onClick={() => handleUploadLogo('secondary')}
                disabled={saving}
                className="wl-btn wl-btn-primary"
              >
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhitelabelSettingsForm;

